import { Vector3 } from 'three';
import { HalfedgeDS } from '../core/HalfedgeDS';
import { Vertex } from '../core/Vertex';

export default function edgeCollapse(struct: HalfedgeDS, source: Vertex, target: Vertex, moveVertexToMidpoint: boolean, debug = false): boolean {
  // 0) Must be adjacent
  const eST = source.getHalfedgeToVertex(target);
  if (!eST) {
    if (debug) console.log(`edgeCollapse: FAIL - no edge between ${source.id} and ${target.id}`);
    return false;
  }

  const facesToSkip = new Set<any>();
  if (eST.face) facesToSkip.add(eST.face);
  if (eST.twin?.face) facesToSkip.add(eST.twin.face);

  // 1) Only allow border or interior edges that pass link condition
  const linkResult = passesLinkCondition(source, target, debug);
  if (!linkResult.passes) {
    if (debug) console.log(`edgeCollapse: FAIL - link condition for ${source.id} → ${target.id}: common=${linkResult.common}, interior=${linkResult.interior}, expected=${linkResult.interior ? '2' : '<=1'}`);
    return false;
  }

  // 2) Compute new position & check flips before committing
  const newPos = moveVertexToMidpoint ? source.position.clone().add(target.position).multiplyScalar(0.5) : target.position.clone();

  // Temporarily test flips using target's 1-ring triangles with new position.
  // (Lightweight: treat "would create degenerate" as flip)
  if (wouldFlipFaces(target, newPos, facesToSkip)) {
    if (debug) console.log(`edgeCollapse: FAIL - would flip faces for ${source.id} → ${target.id}`);
    return false;
  }
  
  if (debug) console.log(`edgeCollapse: SUCCESS - collapsing ${source.id} → ${target.id}`);

  // 3) Collect source neighbors in **robust cyclic order** without relying on prev.twin when on boundary
  const ring = [...source.adjacentVertices()]; // implement robustly in your core:
  // - Start at a boundary halfedge if source is boundary; walk using `next.twin` when available.
  // - Fall back to angle-sorting around source normal if adjacency is incomplete.

  // 4) Remove the two faces incident to (source,target) if they exist (interior) before edge removal
  //    Guard for boundary: one or both may be missing
  const leftFaceHalf = eST;
  const rightFaceHalf = eST.twin;
  if (leftFaceHalf?.face) struct.removeFace(leftFaceHalf.face);
  if (rightFaceHalf?.face) struct.removeFace(rightFaceHalf.face);

  // 5) Remove the edge (source,target)
  struct.removeEdge(eST, false); // don't auto-merge faces

  // 6) Move/merge attributes: color, position, custom data
  target.position.copy(newPos);
  target.color.add(source.color).multiplyScalar(0.5);

  // 7) Rewire: for each neighbor n of source (excluding target)
  //    Ensure (n,target) exists; if not, create it — but ONLY if it won’t duplicate or create a bow-tie.
  const neighbors: Vertex[] = ring.filter((n) => n !== target);

  // Remove faces around source (they’ll be rebuilt); your removeVertex should also nuke dangling halfedges
  struct.removeVertex(source);

  // 8) Rebuild the fan around target with caution
  //    We want faces (target, n_i, n_{i+1}) with consistent winding.
  if (neighbors.length >= 2) {
    // Ensure neighbors form a **simple** loop: no duplicates
    const uniq: Vertex[] = [];
    const seen = new Set<number>();
    for (const n of neighbors) {
      if (!seen.has(n.id)) {
        seen.add(n.id);
        uniq.push(n);
      }
    }

    const firstEdge = ensureEdge(struct, uniq[0], target);
    let prevEdge = firstEdge;
    for (let i = 1; i < uniq.length; i++) {
      const n = uniq[i];
      const edgeNT = ensureEdge(struct, n, target); // creates if absent
      const eNNprev = n.getHalfedgeToVertex(uniq[i - 1]);

      // Form triangle (target, n_{i-1}, n_i) ensuring orientation; try both windings
      tryAddFaceSafe(struct, prevEdge, edgeNT.twin, eNNprev);
      prevEdge = edgeNT;
    }

    // Close loop between last and first if that edge exists
    const eN0Nlast = uniq[0].getHalfedgeToVertex(uniq[uniq.length - 1]);
    if (eN0Nlast) {
      tryAddFaceSafe(struct, prevEdge, firstEdge.twin, eN0Nlast);
    }
  }

  return true;

  // — helpers —

  function ensureEdge(struct: HalfedgeDS, a: Vertex, b: Vertex) {
    // If it exists, reuse; else create
    const e = a.getHalfedgeToVertex(b);
    return e ?? struct.addEdge(a, b);
  }

  function tryAddFaceSafe(struct: HalfedgeDS, e1: any, e2: any, e3?: any) {
    if (!e3) return; // triangle side missing — skip
    const va = e1.vertex,
      vb = e2.vertex,
      vc = e3.vertex;
    // Prevent degenerate: any two equal
    if (va === vb || vb === vc || vc === va) return;

    try {
      struct.addFace([e1, e2, e3]); // preferred winding
    } catch {
      try {
        struct.addFace([e3.twin, e2.twin, e1.twin]); // fallback winding
      } catch {
        /* skip if still invalid */
      }
    }
  }
}

/** Classic edge-link condition for manifold safety. */
function passesLinkCondition(u: Vertex, v: Vertex, debug = false): { passes: boolean; common: number; interior: boolean } {
  const Nu = new Set<Vertex>();
  for (const n of u.adjacentVertices()) if (n !== v) Nu.add(n);

  const Nv = new Set<Vertex>();
  for (const n of v.adjacentVertices()) if (n !== u) Nv.add(n);

  let common = 0;
  const commonVertices: Vertex[] = [];
  for (const n of Nu) {
    if (Nv.has(n)) {
      common++;
      commonVertices.push(n);
    }
  }

  const e = u.getHalfedgeToVertex(v);
  const interior = !!(e && e.face && e.twin && e.twin.face);

  // interior: exactly 2 common neighbors (the two opposite vertices)
  // boundary: at most 1 common neighbor
  const passes = interior ? common === 2 : common <= 1;
  
  if (debug && !passes) {
    console.log(`  linkCondition details: Nu=${Nu.size}, Nv=${Nv.size}, common=${common}, commonIds=[${commonVertices.map(v => v.id).join(',')}], interior=${interior}`);
  }
  
  return { passes, common, interior };
}

/** Prevent triangle flip after moving target by checking signed areas. */
function wouldFlipFaces(
  target: Vertex,
  newPos: Vector3,
  facesToSkip?: Set<any>, // Set<Face>
): boolean {
  const EPS = 1e-12;
  for (const h of target.loopCW()) {
    // Skip triangles we are about to delete
    if (facesToSkip && (facesToSkip.has(h.face) || facesToSkip.has(h.twin?.face))) {
      continue;
    }

    const bV = h.next?.vertex;
    const cV = h.prev?.vertex;
    if (!bV || !cV) continue;

    const a = newPos;
    const b = bV.position;
    const c = cV.position;

    const ab = b.clone().sub(a);
    const ac = c.clone().sub(a);
    const area2 = ab.cross(ac).length();
    if (area2 <= EPS) return true; // near-degenerate or flipped
  }
  return false;
}
